import type { FunctionComponent } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { JobArchitectureItemStatistics } from '~/shared/data-contract/job-architecture';
import { IconJobCluster, IconJobFamily, IconLevel, IconRole, PersonCircleIcon } from '~/ui/core/icons';
import {
    calculateStatsProgress,
    StatsCount,
    StatsProgress,
    StatsRow,
    StatsSeparator,
    statsProgressColors,
} from '~/ui/core/stats/Stats';

export type ArchitectureStatsRowProps = {
    readonly statistics: JobArchitectureItemStatistics;
};

export const ArchitectureStatsRow: FunctionComponent<ArchitectureStatsRowProps> = ({
    statistics,
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Removing complexity isn't worth it
}) => {
    const { translate } = useContext(ApplicationContext);

    const hasHierarchyStats =
        typeof statistics.familiesCount === 'number' &&
        typeof statistics.clustersCount === 'number' &&
        typeof statistics.rolesCount === 'number' &&
        typeof statistics.levelsCount === 'number';

    if (!hasHierarchyStats) {
        return null;
    }

    const rolesCount = statistics.rolesCount ?? 0;
    const levelsCount = statistics.levelsCount ?? 0;

    // One line in the header; in the fixed-width switcher list the count groups wrap instead of being clipped.
    return (
        <StatsRow className="flex-nowrap in-[[role=option]]:flex-wrap">
            <StatsCount
                count={statistics.familiesCount ?? 0}
                data-testid="families-count"
                tooltip={translate('overwrites.jobFamily.many')}
            >
                <IconJobFamily size={12} />
            </StatsCount>
            <StatsCount
                count={statistics.clustersCount ?? 0}
                data-testid="clusters-count"
                tooltip={translate('overwrites.jobCluster.many')}
            >
                <IconJobCluster size={12} />
            </StatsCount>
            <span className="inline-flex items-center gap-s">
                <StatsSeparator />
                <StatsCount
                    count={rolesCount}
                    data-testid="roles-count"
                    tooltip={translate('pages.job-architecture-v2.statistics.role-count')}
                >
                    <IconRole size={12} />
                </StatsCount>
                <StatsCount
                    count={statistics.reviewerRoles ?? 0}
                    data-testid="reviewer-roles-count"
                    tooltip={translate('pages.job-architecture-v2.statistics.roles-with-reviewers')}
                >
                    <PersonCircleIcon />
                </StatsCount>
                <StatsProgress
                    colorBase={statsProgressColors.info.base}
                    colorFill={statsProgressColors.info.fill}
                    count={statistics.approvedRoles ?? 0}
                    data-testid="approved-roles-count"
                    icon="review"
                    progress={calculateStatsProgress(statistics.approvedRoles ?? 0, rolesCount)}
                    tooltip={translate('pages.job-architecture-v2.statistics.reviewed-roles')}
                />
                <StatsProgress
                    colorBase={statsProgressColors.success.base}
                    colorFill={statsProgressColors.success.fill}
                    count={statistics.publishedRoles ?? 0}
                    data-testid="published-roles-count"
                    icon="publish"
                    progress={calculateStatsProgress(statistics.publishedRoles ?? 0, rolesCount)}
                    tooltip={translate('pages.job-architecture-v2.statistics.published-roles')}
                />
            </span>
            <span className="inline-flex items-center gap-s">
                <StatsSeparator />
                <StatsCount
                    count={levelsCount}
                    data-testid="levels-count"
                    tooltip={translate('pages.job-architecture-v2.statistics.level-count')}
                >
                    <IconLevel size={12} />
                </StatsCount>
                <StatsCount
                    count={statistics.reviewerLevels ?? 0}
                    data-testid="reviewer-levels-count"
                    tooltip={translate('pages.job-architecture-v2.statistics.levels-with-reviewers')}
                >
                    <PersonCircleIcon />
                </StatsCount>
                <StatsProgress
                    colorBase={statsProgressColors.info.base}
                    colorFill={statsProgressColors.info.fill}
                    count={statistics.approvedLevels ?? 0}
                    data-testid="approved-levels-count"
                    icon="review"
                    progress={calculateStatsProgress(statistics.approvedLevels ?? 0, levelsCount)}
                    tooltip={translate('pages.job-architecture-v2.statistics.reviewed-levels')}
                />
                <StatsProgress
                    colorBase={statsProgressColors.success.base}
                    colorFill={statsProgressColors.success.fill}
                    count={statistics.publishedLevels ?? 0}
                    data-testid="published-levels-count"
                    icon="publish"
                    progress={calculateStatsProgress(statistics.publishedLevels ?? 0, levelsCount)}
                    tooltip={translate('pages.job-architecture-v2.statistics.published-levels')}
                />
            </span>
        </StatsRow>
    );
};
